import { arrayToEntityDict, type EmbeddedModule, type ModuleBuild, type PlayerBuild } from '@webapp2/shared/model'
import { computed } from 'vue'
import { registeredStateId, state, type BuildDownloadState, type BuildDownloadStatus } from '../state'
import { postGetBody } from './api'
import { cache } from './cache'
import { courseByTechniquePath } from './course-service'
import { registerModules } from './quest-bridge'

export async function ensureModuleBuilds(ids: string[]) {
  const stateBuildIds = new Set(Object.keys(state.moduleBuilds))
  const buildIds = [...new Set(ids)].filter(x => !stateBuildIds.has(x))
  if (buildIds.length === 0) {
    return []
  }
  const response = await postGetBody<ModuleBuild[]>('module-builds', { buildIds })
  if (!response) {
    console.warn('Failed to update module builds')
    return []
  }
  state.moduleBuilds = { ...state.moduleBuilds, ...arrayToEntityDict(response) }
  await cache.setItems(response)
  return response
}

export async function ensureCurrentPlayerBuild() {
  if (state.vuplex?.playerBuildId) {
    await ensurePlayerBuild(state.vuplex?.playerBuildId)
  }
}

export async function ensurePlayerBuild(id: string) {
  if (!state.playerBuilds[id]) {
    await loadPlayerBuilds([id], [])
  }
}

export async function ensurePlayerBuildsForMultiplayerSessions() {
  const idsToLoad = new Set(
    Object.values(state.multiplayerSessions)
      .map(x => x.serverBuildId)
      .filter(x => !playerBuildsByServerBuildId.value[x])
  )
  if (idsToLoad.size === 0) {
    return
  }
  await loadPlayerBuilds([], [...idsToLoad])
}

async function loadPlayerBuilds(playerBuildIds: string[], serverPlayerBuildIds: string[]) {
  const response = await postGetBody<PlayerBuild[]>(`player-builds`, { playerBuildIds, serverPlayerBuildIds })
  if (!response) {
    console.warn('Failed to update player builds')
    return
  }

  await cache.setItems(response)
  state.playerBuilds = { ...state.playerBuilds, ...arrayToEntityDict(response) }
}

export const currentPlayerBuild = computed(() => {
  const id = state.vuplex?.playerBuildId
  return id ? state.playerBuilds[id] : undefined
})

export const currentEmbeddedModules = computed(() =>
  (currentPlayerBuild.value?.embeddedModules ?? []).reduce<{ [techniquePath: string]: EmbeddedModule }>(
    (acc, module) => {
      acc[module.techniquePath] = module
      return acc
    },
    {}
  )
)

export const downloadStatusByBuildId = computed(() => {
  const acc: { [buildId: string]: BuildDownloadStatus } = {}
  for (const build of Object.values(state.moduleBuilds)) {
    const registeredModuleBuild = state.registeredStates[registeredStateId(build.techniquePath)]
    const status =
      registeredModuleBuild?.moduleBuildId === build.id
        ? registeredModuleBuild.downloadStatus
        : {
            id: build.id,
            downloadedBytes: 0,
            totalBytes: 0,
            state: 'NotRegistered' as BuildDownloadState
          }
    acc[build.id] = status
  }
  return acc
})

export const playerBuildsByServerBuildId = computed(() =>
  Object.values(state.playerBuilds).reduce<{ [serverBuildId: string]: PlayerBuild }>((acc, build) => {
    if (build.serverBuildId) {
      const existing = acc[build.serverBuildId]
      acc[build.serverBuildId] = existing && new Date(existing.created) > new Date(build.created) ? existing : build
    }
    return acc
  }, {})
)

export type TechniqueDetails = {
  moduleBuild?: ModuleBuild
  courseBuild?: ModuleBuild
  embeddedModule?: EmbeddedModule
  techniquePath: string
}

const techniquePaths = computed(() => {
  const paths = new Set(Object.values(state.moduleBuilds).map(x => x.techniquePath))
  for (const path of Object.keys(currentEmbeddedModules.value)) {
    paths.add(path)
  }
  return paths
})

export const techniqueDetailsByTechniquePath = computed(() =>
  [...techniquePaths.value].reduce<{ [techniquePath: string]: TechniqueDetails }>((acc, techniquePath) => {
    const embedded = currentEmbeddedModules.value[techniquePath]
    const moduleRegistered = state.registeredStates[registeredStateId(techniquePath)]
    const registeredModuleBuild = moduleRegistered ? state.moduleBuilds[moduleRegistered.moduleBuildId] : undefined
    const courseBuildId = courseByTechniquePath.value[techniquePath]?.buildId
    const courseBuild = courseBuildId ? state.moduleBuilds[courseBuildId] : undefined
    const moduleBuild = registeredModuleBuild ?? courseBuild
    if (!moduleBuild && !embedded) {
      return acc
    }
    if (embedded) {
      acc[techniquePath] = {
        techniquePath,
        embeddedModule: embedded,
        courseBuild
      }
    } else if (moduleBuild) {
      acc[techniquePath] = {
        techniquePath,
        moduleBuild,
        courseBuild
      }
    }
    return acc
  }, {})
)

export function registerModulesForTechniques() {
  const moduleBuilds = Object.values(techniqueDetailsByTechniquePath.value)
    .filter(x => Boolean(x.moduleBuild))
    .map(x => x.moduleBuild!)
  if (moduleBuilds.length) {
    registerModules({
      builds: moduleBuilds
    })
  }
}

export const requiredTutorials = {
  singleplayer: 'Assets/OSSO-FoundationalTutorial/Scenes/FoundationalTutorial-TechniqueBuildData.asset',
  collab: 'Assets/OSSO-FoundationalTutorialCollab/Scenes/FoundationalTutorialCollab-TechniqueBuildData.asset'
}
